/**
 * PoFinishedDialog — đổi trạng thái Processing↔Finished cho 1 PO ở /factory/<code>/po-list.
 *
 * Thay cho popup confirm thuần: khi → Finished mở 1 form nhỏ có PO Closing Date
 * mặc định HÔM NAY, cho phép sửa rồi mới Save. Khi → Processing chỉ confirm mở
 * khoá (clear closing date). Save xong đi chung event `freeze_po_edit_on_finish`
 * (giống nút status bên Batch Report).
 */
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from '@mui/material'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import dayjs from 'dayjs'
import { useEffect, useState } from 'react'

export interface PoFinishedDialogProps {
  open: boolean
  /** true = đang đổi sang Finished (hiện field Closing Date), false = về Processing. */
  toFinished: boolean
  poNo: string
  saving?: boolean
  onClose: () => void
  /** Save: trả closing date (YYYY-MM-DD) khi Finished; null khi Processing. */
  onConfirm: (closingDate: string | null) => void
}

export default function PoFinishedDialog({
  open,
  toFinished,
  poNo,
  saving,
  onClose,
  onConfirm,
}: PoFinishedDialogProps) {
  // Default Closing Date = hôm nay; cho phép user chỉnh trước khi Save.
  const [closingDate, setClosingDate] = useState(() => dayjs())

  // Mỗi lần mở lại (Finished) reset về hôm nay để không giữ giá trị cũ.
  useEffect(() => {
    if (open && toFinished) setClosingDate(dayjs())
  }, [open, toFinished])

  const handleSave = () => {
    if (saving) return
    if (toFinished) {
      const valid = closingDate && closingDate.isValid() ? closingDate : dayjs()
      onConfirm(valid.format('YYYY-MM-DD'))
    } else {
      onConfirm(null)
    }
  }

  return (
    <Dialog open={open} onClose={() => !saving && onClose()} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ fontWeight: 800 }}>
        {toFinished ? 'Mark as Finished' : 'Mark as Processing?'}
      </DialogTitle>
      <DialogContent>
        {toFinished ? (
          <>
            <DialogContentText sx={{ fontSize: '0.85rem', mb: 2 }}>
              Mark PO {poNo} as Finished? This closes the batch report and logs all
              current values for this PO — you can&apos;t change them afterwards.
            </DialogContentText>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                label="PO Closing Date"
                format="DD-MMM-YYYY"
                value={closingDate}
                onChange={(d) => d && setClosingDate(d)}
                slotProps={{
                  textField: {
                    size: 'small',
                    fullWidth: true,
                    InputLabelProps: { shrink: true },
                  },
                }}
              />
            </LocalizationProvider>
          </>
        ) : (
          <DialogContentText sx={{ fontSize: '0.85rem' }}>
            Change PO {poNo} status back to Processing? This reopens the batch report
            so you can edit the previously entered values again.
          </DialogContentText>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={saving} sx={{ textTransform: 'none' }}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color={toFinished ? 'success' : 'warning'}
          disabled={saving}
          startIcon={saving ? <CircularProgress size={14} color="inherit" /> : undefined}
          onClick={handleSave}
          sx={{ textTransform: 'none', fontWeight: 700 }}
        >
          {saving ? 'Saving…' : 'Confirm'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
